import { createAuthClient } from '@neondatabase/neon-js/auth';

// Build-time configuration injected by Vite (see .env.example / Pages env vars).
// The Neon Auth base URL is public by design: identity lives in the database and
// the connection string is never part of the browser bundle.
//
// Every Neon Auth route lives under `<host>/neondb/auth/...` — that is the
// "Auth base URL" the Neon Console hands you, and it is the value the edge API
// (functions/api/_shared/db.js) and the dev-user script use.
const authRoot = String(import.meta.env.VITE_NEON_AUTH_URL ?? '').trim().replace(/\/+$/, '');

// The Neon Auth browser SDK, however, is inconsistent about that base:
// `client.auth.signIn.email()` / `signUp.email()` build `<base>/auth/...`,
// while `client.getSession()` / `client.token()` build `<base>/...`. So the
// console URL works for one group and 404s the other (Neon answers an unknown
// path with `404 user_not_found`, which reads like a bad password but is a
// routing failure). Handing the SDK the database root makes its better-auth
// calls land on `.../neondb/auth/sign-in/email`; the two non-better-auth calls
// are then issued directly against the real root below.
const authClientBase = authRoot.replace(/\/auth$/, '');

/** True when the Neon Auth build-time variable is present. */
export const isAuthConfigured = Boolean(authRoot);

/**
 * Shared Neon Auth browser client (null when the app is not configured).
 *
 * `credentials: 'include'` matters: Neon Auth authenticates with an HTTP-only
 * session cookie, so cross-origin calls (a Vite dev server, or a Pages
 * deployment on a different host than the Auth URL) must send the cookie or
 * `getSession()`/`/token` answer 401 and every API call is rejected.
 */
export const authClient = isAuthConfigured
  ? createAuthClient(authClientBase, { fetchOptions: { credentials: 'include' } })
  : null;

/**
 * GET one of Neon Auth's own routes, sending the session cookie.
 *
 * `fetchAuthSession` / `fetchAuthToken` replace `client.getSession()` and
 * `client.token()`: those two are the only SDK methods that do not insert the
 * `/auth` segment, so they 404 against this deployment. Everything else keeps
 * using the SDK.
 *
 * @param {string} path e.g. '/get-session' or '/token'
 * @returns {Promise<any>} the parsed body, or null when the request fails
 */
async function getAuthRoute(path) {
  try {
    const response = await fetch(`${authRoot}${path}`, {
      headers: { accept: 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * The current session, or null when signed out.
 *
 * Neon answers an expired/absent session with HTTP 200 and a `null` body, so
 * the null check — not the status code — is the real test.
 * @returns {Promise<{user: {id: string, email?: string}} | null>}
 */
export async function fetchAuthSession() {
  return getAuthRoute('/get-session');
}

/**
 * The short-lived JWT the edge API expects as a bearer token.
 *
 * `/token` mints a fresh JWT from the session cookie; it is deliberately not
 * cached here because the token expires in 15 minutes and callers fetch it on
 * demand.
 * @returns {Promise<string | null>}
 */
export async function fetchAuthToken() {
  const payload = await getAuthRoute('/token');
  return typeof payload?.token === 'string' ? payload.token : null;
}

/**
 * Access the client or fail with an actionable message.
 * @returns {NonNullable<typeof authClient>}
 */
export function requireAuth() {
  if (!authClient) {
    throw new Error('Neon Auth is not configured. Set VITE_NEON_AUTH_URL to enable accounts.');
  }
  return authClient;
}
